interface PricingCardsProps {
  billingCycle: "monthly" | "annual";
}

export default function PricingCards({ billingCycle }: PricingCardsProps) {
  const plans = [
    {
      name: "Free",
      tagline: "Perfect for student pilots",
      price: { monthly: 0, annual: 0 },
      features: [
        { text: "Basic flight scoring", included: true },
        { text: "Up to 5 flights per month", included: true },
        { text: "Radio transcription", included: true },
        { text: "Flight replay viewer", included: true },
        { text: "Pattern analysis", included: false },
        { text: "Coaching insights", included: false },
        { text: "Progress tracking", included: false },
        { text: "Community replays", included: false },
      ],
      cta: "Get Started Free",
      popular: false,
    },
    {
      name: "Pro",
      tagline: "For serious pilots",
      price: { monthly: 14.99, annual: 11.99 },
      features: [
        { text: "Advanced flight scoring", included: true },
        { text: "Unlimited flights", included: true },
        { text: "Radio transcription", included: true },
        { text: "Flight replay theater", included: true },
        { text: "Pattern analysis", included: true },
        { text: "AI coaching insights", included: true },
        { text: "Progress tracking dashboard", included: true },
        { text: "Community replays access", included: true },
      ],
      cta: "Start Pro Trial",
      popular: true,
    },
    {
      name: "Instructor",
      tagline: "Built for CFIs & schools",
      price: { monthly: 29.99, annual: 23.99 },
      features: [
        { text: "Everything in Pro", included: true },
        { text: "Student management dashboard", included: true },
        { text: "Multi-student tracking", included: true },
        { text: "Custom lesson plans", included: true },
        { text: "Detailed performance reports", included: true },
        { text: "White-label branding", included: true },
        { text: "Priority support", included: true },
        { text: "API access", included: true },
      ],
      cta: "Get Instructor Access",
      popular: false,
    },
  ];

  return (
    <section className="relative py-20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-white/5 backdrop-blur-sm border rounded-2xl p-8 transition-all hover:scale-105 hover:shadow-2xl ${
                plan.popular
                  ? "border-[#00FFD1] shadow-[#00FFD1]/20 shadow-xl"
                  : "border-white/10"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#00FFD1] to-[#00A3E0] text-[#0A1628] text-xs font-bold px-6 py-2 rounded-full uppercase tracking-wider">
                  Most Popular
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <p className="text-sm text-gray-400 mb-6">{plan.tagline}</p>
                
                <div className="mb-6">
                  <span className="text-5xl font-bold">
                    ${billingCycle === "monthly" ? plan.price.monthly : plan.price.annual}
                  </span>
                  {plan.price.monthly > 0 && (
                    <span className="text-gray-400 text-sm ml-2">/month</span>
                  )}
                </div>

                {billingCycle === "annual" && plan.price.monthly > 0 && (
                  <p className="text-xs text-[#00FFD1] mb-4">
                    Billed ${(plan.price.annual * 12).toFixed(2)} annually
                  </p>
                )}

                <button
                  className={`w-full py-4 rounded-lg font-bold text-sm transition-all cursor-pointer whitespace-nowrap ${
                    plan.popular
                      ? "bg-[#00A3E0] hover:bg-[#0088BB] text-white shadow-lg hover:shadow-[#00A3E0]/40"
                      : "bg-white/10 hover:bg-white/20 text-white border border-white/20"
                  }`}
                >
                  {plan.cta}
                </button>
              </div>

              <div className="space-y-4">
                {plan.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    {feature.included ? (
                      <i className="ri-checkbox-circle-fill text-[#00FFD1] text-lg mt-0.5 flex-shrink-0"></i>
                    ) : (
                      <i className="ri-close-circle-line text-gray-600 text-lg mt-0.5 flex-shrink-0"></i>
                    )}
                    <span className={`text-sm ${feature.included ? "text-gray-300" : "text-gray-600"}`}>
                      {feature.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}